import { Loader2 } from "lucide-react";
import { ReportStatus } from "@/graphql/generated";
import { useLocale } from "@/context/LanguageContext";

interface Props {
  status: ReportStatus;
}

/**
 * Coloured badge for the OrgReport row. The status classes use semantic
 * theme tokens with `dark:` overrides so both light and dark modes pick
 * up correct contrast (the rest of the portal — RosterRowStatusBadge,
 * QueueStatusBadge, etc. — follows the same pattern).
 *
 * The `GENERATING` state shows a spinner so a user mid-poll knows the
 * row is alive.
 */
export function ReportStatusBadge({ status }: Props) {
  const { t } = useLocale();

  const config = {
    [ReportStatus.Pending]: {
      label: t("reports.status.pending"),
      classes:
        "bg-muted text-muted-foreground",
      spinner: true,
    },
    [ReportStatus.Generating]: {
      label: t("reports.status.generating"),
      classes:
        "bg-warning/15 text-warning dark:bg-warning/20 dark:text-warning",
      spinner: true,
    },
    [ReportStatus.Complete]: {
      label: t("reports.status.complete"),
      classes:
        "bg-success/15 text-success dark:bg-success/20 dark:text-success",
      spinner: false,
    },
    [ReportStatus.Failed]: {
      label: t("reports.status.failed"),
      classes:
        "bg-destructive/15 text-destructive dark:bg-destructive/20",
      spinner: false,
    },
  }[status];

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${config.classes}`}
    >
      {config.spinner ? (
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
      ) : null}
      {config.label}
    </span>
  );
}
